<template>
  <div class="px-2 py-2">
    <woot-loading-state
      v-if="isLoading"
      :message="$t('QUEUES_MGMT.TIMELINE.LOADING')"
    />
    <template v-else>
      <!-- QM §5.6.2 — assignment metadata, derived from the audit trail below -->
      <dl
        v-if="assignment"
        class="text-xs text-slate-600 dark:text-slate-400 grid gap-0.5 mb-3 pb-3 border-b border-slate-100 dark:border-slate-800"
      >
        <div class="flex justify-between">
          <dt>{{ $t('QUEUES_MGMT.METADATA.ASSIGNED_AT') }}</dt>
          <dd>{{ formatTime(assignment.at) }}</dd>
        </div>
        <div v-if="assignment.via" class="flex justify-between">
          <dt>{{ $t('QUEUES_MGMT.METADATA.VIA') }}</dt>
          <dd>
            {{
              $t(
                `QUEUES_MGMT.SUPERVISOR.AGENTS_TABLE.GROUP.${assignment.via.toUpperCase()}`
              )
            }}
          </dd>
        </div>
        <div v-if="assignment.expansion" class="flex justify-between">
          <dt>{{ $t('QUEUES_MGMT.METADATA.EXPANSION') }}</dt>
          <dd>{{ expansionLabel(assignment.expansion) }}</dd>
        </div>
        <div
          v-if="assignment.outside"
          class="text-yellow-700 dark:text-yellow-500"
        >
          {{ $t('QUEUES_MGMT.SUPERVISOR.ASSIGN.OUTSIDE_BADGE') }}
        </div>
      </dl>

      <p v-if="!entries.length" class="text-sm text-slate-500">
        {{ $t('QUEUES_MGMT.TIMELINE.EMPTY') }}
      </p>
      <ol v-else class="flex flex-col gap-3">
        <li
          v-for="entry in entries"
          :key="entry.id"
          class="flex flex-col border-s-2 border-slate-100 dark:border-slate-800 ps-3"
        >
          <span class="text-sm font-medium text-slate-800 dark:text-slate-100">
            {{ eventLabel(entry.event_type) }}
          </span>
          <span class="text-sm text-slate-600 dark:text-slate-400">
            {{ description(entry) }}
          </span>
          <span class="text-xs text-slate-400">
            {{ formatTime(entry.created_at) }} ·
            {{ entry.actor_name || $t('QUEUES_MGMT.TIMELINE.SYSTEM') }}
          </span>
        </li>
      </ol>
    </template>
  </div>
</template>

<script>
import { format, parseISO } from 'date-fns';
import alertMixin from 'shared/mixins/alertMixin';
import QueueActionsAPI from 'dashboard/api/queueActions';

const ASSIGNMENT_EVENTS = [
  'automatic_assignment',
  'manual_assignment',
  'manual_pull',
];

export default {
  mixins: [alertMixin],
  props: {
    // ContactPanel passes the display_id, which arrives as a string on some routes.
    conversationId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      entries: [],
      isLoading: false,
    };
  },
  computed: {
    // QM §5.6.2 — metadata of the most recent assignment. Entries arrive oldest-first,
    // so the last matching one is the current owner's assignment.
    assignment() {
      const last = [...this.entries]
        .reverse()
        .find(entry => ASSIGNMENT_EVENTS.includes(entry.event_type));
      if (!last) return null;

      const payload = last.payload || {};
      const expansion = payload.expansion_trigger;
      return {
        at: last.created_at,
        via: payload.assigned_via,
        // Shown only when the pool was actually expanded at assignment time.
        expansion: expansion && expansion !== 'none' ? expansion : null,
        outside: !!payload.outside_routing_groups,
      };
    },
  },
  watch: {
    conversationId: {
      immediate: true,
      handler() {
        this.load();
      },
    },
  },
  methods: {
    async load() {
      if (!this.conversationId) {
        return;
      }
      this.isLoading = true;
      try {
        const { data } = await QueueActionsAPI.activity(this.conversationId);
        this.entries = data.payload;
      } catch (error) {
        // A ticket outside the queue engine simply has no history to show.
        this.entries = [];
      } finally {
        this.isLoading = false;
      }
    },
    formatTime(value) {
      return value ? format(parseISO(value), 'dd/MM/yy HH:mm') : '';
    },
    eventLabel(eventType) {
      return this.$t(
        `QUEUES_MGMT.AUDIT.EVENT_TYPES.${eventType.toUpperCase()}`
      );
    },
    // QM §5.6.4 — one short factual sentence per entry, built from the audit payload.
    description(entry) {
      const { event_type: type, agent_name: agent, queue_name: queue } = entry;
      const payload = entry.payload || {};
      switch (type) {
        case 'automatic_assignment':
          return this.$t('QUEUES_MGMT.TIMELINE.DESC.AUTOMATIC_ASSIGNMENT', {
            agent,
            via: payload.assigned_via,
          });
        case 'manual_assignment':
        case 'manual_pull':
          return this.$t('QUEUES_MGMT.TIMELINE.DESC.ASSIGNED_TO', { agent });
        case 'queue_changed':
          return this.$t('QUEUES_MGMT.TIMELINE.DESC.QUEUE_CHANGED', { queue });
        case 'returned_to_queue':
          return this.$t('QUEUES_MGMT.TIMELINE.DESC.RETURNED', {
            queue,
            reason: payload.reason,
          });
        case 'auto_returned':
          return this.$t('QUEUES_MGMT.TIMELINE.DESC.AUTO_RETURNED', {
            seconds: payload.inactivity_seconds,
          });
        case 'overflow_expansion_fired':
          return this.$t('QUEUES_MGMT.TIMELINE.DESC.EXPANSION', {
            trigger: payload.trigger,
          });
        case 'lock_applied':
          return this.$t('QUEUES_MGMT.TIMELINE.DESC.LOCK_APPLIED', { agent });
        case 'lock_released':
          return this.$t('QUEUES_MGMT.TIMELINE.DESC.LOCK_RELEASED', {
            reason: payload.reason,
          });
        case 'left_unassigned':
          return this.$t('QUEUES_MGMT.TIMELINE.DESC.LEFT_UNASSIGNED', {
            queue,
          });
        default:
          return '';
      }
    },
  },
};
</script>
